/**
 * MyOnboardingPage — Redesigned onboarding experience for new hires.
 *
 * Inspired by Gusto/BambooHR onboarding portals:
 * - Warm welcome banner with progress ring
 * - Phase navigation tabs (Pre-Start, Day 1, Week 1, Month 1)
 * - Clean task cards with clear CTAs
 * - Celebration state when complete
 */
import { useState, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Stack, Chip, Avatar, Card, CardContent,
  Button, Skeleton, Fade,
  TextField, alpha, CircularProgress, Tab, Tabs,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  CheckCircle as DoneIcon,
  RadioButtonUnchecked as TodoIcon,
  Warning as OverdueIcon,
  Upload as UploadIcon,
  OpenInNew as LinkIcon,
  Description as DocIcon,
  Assignment as FormIcon,
  PlayCircle as ContentIcon,
  EmojiEvents as TrophyIcon,
  Celebration as CelebrationIcon,
  Schedule as ClockIcon,
  Person as PersonIcon,
  Business as CompanyIcon,
  CloudUpload as CloudIcon,
  TaskAlt as TaskIcon,
  ArrowForward as ArrowIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
  onboardingApi,
  type OnboardingTask,
  type OnboardingInstance,
} from '@/api/endpoints/hr';
import { useNotificationStore } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import apiClient from '@/api/client';
import { employeesApi } from '@/api/endpoints/hr';
import { TYPE } from '@/theme/typography';
import { ONBOARDING_PHASE_COLORS } from '@/theme/colorTokens';
import { formatDate, resolveUploadUrl } from '@/utils/format';
import PageHelpIcon from '@/components/PageHelpIcon';

// ── Phase config ──

const PHASES = [
  {
    key: 'pre_start',
    label: 'Pre-Start',
    subtitle: 'Before your first day',
    icon: <DocIcon />,
    color: ONBOARDING_PHASE_COLORS[0],
    tip: 'Complete these items before you arrive so Day 1 is smooth.',
  },
  {
    key: 'day_one',
    label: 'Day 1',
    subtitle: 'Your first day',
    icon: <CompanyIcon />,
    color: ONBOARDING_PHASE_COLORS[1],
    tip: 'Welcome! Get oriented, meet the team, and settle in.',
  },
  {
    key: 'week_one',
    label: 'Week 1',
    subtitle: 'Getting settled',
    icon: <PersonIcon />,
    color: ONBOARDING_PHASE_COLORS[2],
    tip: 'Dive deeper into your role and set goals with your manager.',
  },
  {
    key: 'month_one',
    label: 'Month 1',
    subtitle: 'First month milestones',
    icon: <TrophyIcon />,
    color: ONBOARDING_PHASE_COLORS[3],
    tip: 'Check in with HR and your manager on how things are going.',
  },
] as const;

const TASK_ICONS: Record<string, React.ReactNode> = {
  document: <CloudIcon sx={{ fontSize: 20 }} />,
  form: <FormIcon sx={{ fontSize: 20 }} />,
  content: <ContentIcon sx={{ fontSize: 20 }} />,
  checklist: <TaskIcon sx={{ fontSize: 20 }} />,
  approval: <TaskIcon sx={{ fontSize: 20 }} />,
};

const ASSIGNEE_LABELS: Record<string, { label: string; color: string }> = {
  employee: { label: 'You', color: ONBOARDING_PHASE_COLORS[0] },
  hr: { label: 'People', color: ONBOARDING_PHASE_COLORS[1] },
  manager: { label: 'Manager', color: ONBOARDING_PHASE_COLORS[2] },
  it: { label: 'IT', color: ONBOARDING_PHASE_COLORS[4] },
  buddy: { label: 'Buddy', color: ONBOARDING_PHASE_COLORS[3] },
  finance: { label: 'Finance', color: ONBOARDING_PHASE_COLORS[5] },
};

// ── Helpers ──

function formatDueDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.ceil((d.getTime() - now.getTime()) / 86400000);
  if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`;
  if (diffDays === 0) return 'Due today';
  if (diffDays === 1) return 'Due tomorrow';
  if (diffDays <= 7) return `Due in ${diffDays} days`;
  return formatDate(dateStr);
}

function isOverdue(task: OnboardingTask): boolean {
  if (task.status === 'completed' || task.status === 'skipped') return false;
  if (!task.due_date) return false;
  return new Date(task.due_date) < new Date();
}

// ── Progress Ring ──

function ProgressRing({ value, size = 64 }: { value: number; size?: number }) {
  const strokeWidth = size > 80 ? 8 : 6;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  const color = value >= 100 ? ONBOARDING_PHASE_COLORS[3] : value >= 60 ? ONBOARDING_PHASE_COLORS[1] : value >= 30 ? ONBOARDING_PHASE_COLORS[2] : ONBOARDING_PHASE_COLORS[5];

  return (
    <Box sx={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="currentColor" strokeWidth={strokeWidth}
          style={{ color: 'var(--mui-palette-divider, #E5E7EB)' }}
        />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <Box sx={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography sx={{ fontSize: size * 0.28, fontWeight: TYPE.bold, lineHeight: 1, color }}>
          {value}%
        </Typography>
        <Typography sx={{ fontSize: size * 0.11, color: 'text.secondary', fontWeight: TYPE.medium }}>
          complete
        </Typography>
      </Box>
    </Box>
  );
}

// ── Task Item ──

function TaskItem({
  task,
  phaseColor,
  onComplete,
  onUpload,
  isCompletePending,
}: {
  task: OnboardingTask;
  phaseColor: string;
  onComplete: (taskId: string, formData?: Record<string, unknown>) => void;
  onUpload: (taskId: string, file: File) => void;
  isCompletePending: boolean;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const isDone = task.status === 'completed' || task.status === 'skipped';
  const overdue = isOverdue(task);
  const assignee = ASSIGNEE_LABELS[task.assignee_type] || ASSIGNEE_LABELS.employee;
  const isMyTask = task.assignee_type === 'employee';

  // ── Compact single-row layout for completed tasks ──
  if (isDone) {
    return (
      <Stack
        direction="row" alignItems="center" gap={1.5}
        sx={{
          px: 1.5, py: 1, borderRadius: 1.5,
          border: '1px solid', borderColor: 'divider',
          bgcolor: (t) => alpha(t.palette.success.main, 0.02),
          opacity: 0.7,
        }}
      >
        <DoneIcon sx={{ color: 'success.main', fontSize: 20, flexShrink: 0 }} />
        <Typography
          sx={{
            flex: 1, minWidth: 0, fontWeight: TYPE.medium, fontSize: TYPE.sm,
            textDecoration: 'line-through', color: 'text.secondary',
          }}
          noWrap
        >
          {task.title}
        </Typography>
        <Chip
          label={assignee.label} size="small" variant="outlined"
          sx={{ height: 18, fontSize: TYPE.xs, fontWeight: TYPE.semibold, borderColor: assignee.color, color: assignee.color }}
        />
        {task.completed_at && (
          <Typography sx={{ fontSize: TYPE.xs, color: 'text.disabled', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {formatDate(task.completed_at)}
            {task.status === 'skipped' && ' · Skipped'}
          </Typography>
        )}
      </Stack>
    );
  }

  // ── Compact single-row for non-employee waiting tasks ──
  if (!isMyTask) {
    return (
      <Stack
        direction="row" alignItems="center" gap={1.5}
        sx={{
          px: 1.5, py: 1, borderRadius: 1.5,
          border: '1px solid',
          borderColor: overdue ? 'error.light' : 'divider',
          bgcolor: overdue ? (t) => alpha(t.palette.error.main, 0.03) : 'background.paper',
        }}
      >
        {overdue
          ? <OverdueIcon sx={{ color: 'error.main', fontSize: 20, flexShrink: 0 }} />
          : <TodoIcon sx={{ fontSize: 20, color: 'text.disabled', flexShrink: 0 }} />
        }
        <Typography sx={{ flex: 1, minWidth: 0, fontWeight: TYPE.medium, fontSize: TYPE.sm, color: 'text.primary' }} noWrap>
          {task.title}
        </Typography>
        {task.is_required && (
          <Chip label="Required" size="small" color="error" variant="filled"
            sx={{ height: 18, fontSize: TYPE.xs, fontWeight: TYPE.semibold }} />
        )}
        <Chip
          label={`Waiting for ${assignee.label}`} size="small" variant="outlined"
          sx={{ height: 18, fontSize: TYPE.xs, fontWeight: TYPE.semibold, borderColor: assignee.color, color: assignee.color }}
        />
        {task.due_date && (
          <Typography sx={{
            fontSize: TYPE.xs, whiteSpace: 'nowrap', flexShrink: 0,
            color: overdue ? 'error.main' : 'text.disabled',
            fontWeight: overdue ? 600 : 400,
          }}>
            {formatDueDate(task.due_date)}
          </Typography>
        )}
      </Stack>
    );
  }

  // ── Active employee task layout ──
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        px: 1.5, py: 1.25,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: overdue ? 'error.light' : 'divider',
        bgcolor: overdue ? (t) => alpha(t.palette.error.main, 0.03) : 'background.paper',
        transition: 'all 0.2s',
        '&:hover': { borderColor: phaseColor, boxShadow: `0 0 0 1px ${phaseColor}20` },
      }}
    >
      {/* Status icon */}
      <Box sx={{ pt: 0.25, flexShrink: 0 }}>
        {overdue ? (
          <OverdueIcon sx={{ color: 'error.main', fontSize: 22 }} />
        ) : (
          <Box sx={{
            width: 22, height: 22, borderRadius: '50%',
            border: '2px solid', borderColor: phaseColor,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {TASK_ICONS[task.task_type] || <TodoIcon sx={{ fontSize: 12, color: phaseColor }} />}
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Title row */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography sx={{ fontWeight: TYPE.semibold, fontSize: TYPE.sm, color: 'text.primary' }} noWrap>
            {task.title}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
            {task.is_required && (
              <Chip label="Required" size="small" color="error" variant="filled"
                sx={{ height: 18, fontSize: TYPE.xs, fontWeight: TYPE.semibold }} />
            )}
            <Chip
              label={assignee.label} size="small" variant="outlined"
              sx={{ height: 18, fontSize: TYPE.xs, fontWeight: TYPE.semibold, borderColor: assignee.color, color: assignee.color }}
            />
            {task.due_date && (
              <Stack direction="row" alignItems="center" gap={0.25}>
                <ClockIcon sx={{ fontSize: 12, color: overdue ? 'error.main' : 'text.disabled' }} />
                <Typography sx={{
                  fontSize: TYPE.xs, whiteSpace: 'nowrap',
                  color: overdue ? 'error.main' : 'text.disabled',
                  fontWeight: overdue ? 600 : 400,
                }}>
                  {formatDueDate(task.due_date)}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>

        {/* Description — single line */}
        {task.description && (
          <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', lineHeight: 1.4, mt: 0.25 }} noWrap>
            {task.description}
          </Typography>
        )}

        {/* Document upload area */}
        {task.task_type === 'document' && (() => {
          const docs = task.documents || [];
          const approved = docs.filter(d => d.status === 'approved');
          const pending = docs.filter(d => d.status === 'uploaded');
          const rejected = docs.filter(d => d.status === 'rejected');
          const hasApproved = approved.length > 0;
          const hasPending = pending.length > 0;
          const hasRejected = rejected.length > 0;

          return (
            <Box sx={{ mt: 0.75 }}>
              {/* Document status — compact inline */}
              {docs.length > 0 && (
                <Stack spacing={0.5} sx={{ mb: 0.75 }}>
                  {docs.map(doc => (
                    <Stack key={doc.id} direction="row" alignItems="center" gap={1}
                      sx={{
                        px: 1, py: 0.5, borderRadius: 1,
                        border: '1px solid',
                        borderColor: doc.status === 'approved' ? 'success.light'
                          : doc.status === 'rejected' ? 'error.light' : 'divider',
                        bgcolor: doc.status === 'approved' ? (t) => alpha(t.palette.success.main, 0.04)
                          : doc.status === 'rejected' ? (t) => alpha(t.palette.error.main, 0.04)
                          : 'transparent',
                      }}
                    >
                      <DocIcon sx={{
                        fontSize: 16,
                        color: doc.status === 'approved' ? 'success.main'
                          : doc.status === 'rejected' ? 'error.main' : 'text.secondary',
                      }} />
                      <Typography sx={{ flex: 1, fontSize: TYPE.xs, fontWeight: TYPE.medium }} noWrap>
                        {doc.file_name}
                      </Typography>
                      {doc.status === 'rejected' && doc.rejection_reason && (
                        <Typography sx={{ fontSize: TYPE.xs, color: 'error.main' }} noWrap>
                          {doc.rejection_reason}
                        </Typography>
                      )}
                      <Chip
                        label={doc.status === 'approved' ? 'Approved' : doc.status === 'rejected' ? 'Rejected' : 'Reviewing'}
                        size="small"
                        color={doc.status === 'approved' ? 'success' : doc.status === 'rejected' ? 'error' : 'default'}
                        variant="filled"
                        sx={{ height: 18, fontSize: TYPE.xs, fontWeight: TYPE.semibold }}
                      />
                    </Stack>
                  ))}
                </Stack>
              )}

              {/* Brief status text */}
              {hasPending && !hasApproved && (
                <Typography sx={{ fontSize: TYPE.xs, color: 'info.main', mb: 0.5 }}>
                  Under HR review — you'll be notified when approved.
                </Typography>
              )}
              {hasRejected && !hasApproved && !hasPending && (
                <Typography sx={{ fontSize: TYPE.xs, color: 'warning.main', mb: 0.5 }}>
                  Rejected — please re-upload a corrected document.
                </Typography>
              )}

              {/* Upload button */}
              {(!hasApproved && (!hasPending || hasRejected)) && (
                <>
                  <input
                    type="file" hidden ref={fileRef}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onUpload(task.id, f);
                    }}
                  />
                  <Button
                    size="small" startIcon={<UploadIcon />} variant="outlined"
                    onClick={() => fileRef.current?.click()}
                    sx={{ borderRadius: 6, textTransform: 'none', fontWeight: TYPE.semibold, fontSize: TYPE.xs }}
                  >
                    {docs.length > 0 ? 'Re-upload' : 'Upload'}
                  </Button>
                </>
              )}
            </Box>
          );
        })()}

        {/* Form fields */}
        {task.task_type === 'form' && task.form_fields && (
          <Box sx={{ mt: 0.75 }}>
            {((task.form_fields as Record<string, unknown>)?.fields as Array<{ name: string; label: string; required?: boolean }> || []).map((field) => (
              <TextField
                key={field.name} label={field.label} size="small" fullWidth
                required={field.required} sx={{ mb: 0.75 }}
                value={formValues[field.name] || ''}
                onChange={(e) => setFormValues(prev => ({ ...prev, [field.name]: e.target.value }))}
              />
            ))}
          </Box>
        )}

        {/* Content link */}
        {task.task_type === 'content' && task.content_url && (
          <Button
            size="small" startIcon={<LinkIcon />} variant="outlined"
            href={task.content_url} target="_blank"
            sx={{ mt: 0.5, borderRadius: 6, textTransform: 'none', fontWeight: TYPE.semibold, fontSize: TYPE.xs }}
          >
            View Material
          </Button>
        )}

        {/* Action button */}
        {task.task_type !== 'document' && (
          <Button
            size="small" variant="contained"
            onClick={() => {
              const fd = task.task_type === 'form' && Object.keys(formValues).length > 0
                ? formValues as Record<string, unknown>
                : undefined;
              onComplete(task.id, fd);
            }}
            disabled={isCompletePending}
            startIcon={isCompletePending ? <CircularProgress size={14} /> : <DoneIcon />}
            sx={{
              mt: 0.5, borderRadius: 6, textTransform: 'none', fontWeight: TYPE.semibold, fontSize: TYPE.xs,
              bgcolor: phaseColor, '&:hover': { bgcolor: phaseColor, filter: 'brightness(0.9)' },
            }}
          >
            {isCompletePending ? 'Saving...' : 'Mark Complete'}
          </Button>
        )}

        {/* Notes */}
        {task.notes && (
          <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px dashed', borderColor: 'divider' }}>
            {task.notes.split('\n').map((line, i) => (
              <Typography key={i} sx={{ fontSize: TYPE.xs, color: 'text.secondary', lineHeight: 1.5 }}>
                {line}
              </Typography>
            ))}
          </Box>
        )}

        <NoteInput taskId={task.id} />
      </Box>
    </Box>
  );
}

/** Inline note input for HR ↔ employee communication on a task */
function NoteInput({ taskId }: { taskId: string }) {
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotificationStore();

  const mutation = useMutation({
    mutationFn: (text: string) => onboardingApi.addNote(taskId, text),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-onboarding'] });
      showSuccess('Note added');
      setNote('');
      setOpen(false);
    },
    onError: () => showError('Failed to add note'),
  });

  if (!open) {
    return (
      <Button
        size="small" variant="text"
        onClick={() => setOpen(true)}
        sx={{ mt: 0.5, fontSize: TYPE.xs, textTransform: 'none', color: 'text.secondary' }}
      >
        + Add a note or question
      </Button>
    );
  }

  return (
    <Stack direction="row" gap={1} sx={{ mt: 1 }}>
      <TextField
        size="small" fullWidth placeholder="Type a note or question for HR..."
        value={note} onChange={(e) => setNote(e.target.value)}
        sx={{ '& .MuiInputBase-root': { fontSize: TYPE.sm } }}
        onKeyDown={(e) => { if (e.key === 'Enter' && note.trim()) mutation.mutate(note.trim()); }}
      />
      <Button
        size="small" variant="contained" disabled={!note.trim() || mutation.isPending}
        onClick={() => mutation.mutate(note.trim())}
        sx={{ borderRadius: 6, textTransform: 'none', minWidth: 'auto', px: 2 }}
      >
        {mutation.isPending ? '...' : 'Send'}
      </Button>
    </Stack>
  );
}


// ── Team Introduction ──

function TeamIntroduction() {
  const { data: teamMembers, isLoading } = useQuery({
    queryKey: ['employees', 0, ''],
    queryFn: () => employeesApi.list({ per_page: 50, status: 'active' }),
    select: (res) => (res.data?.items || []).slice(0, 8),
  });

  if (isLoading || !teamMembers || teamMembers.length === 0) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
        <PersonIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h6" fontWeight={TYPE.bold}>Meet Your Team</Typography>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
        {teamMembers.map((emp: any) => (
          <Card key={emp.id} variant="outlined" sx={{ textAlign: 'center' }}>
            <CardContent sx={{ py: 2, px: 1.5, '&:last-child': { pb: 2 } }}>
              <Avatar
                src={resolveUploadUrl(emp.avatar_url)}
                sx={{
                  width: 48, height: 48, mx: 'auto', mb: 1,
                  bgcolor: 'primary.main', fontSize: TYPE.md, fontWeight: TYPE.semibold,
                }}
              >
                {`${emp.first_name?.[0] || ''}${emp.last_name?.[0] || ''}`.toUpperCase()}
              </Avatar>
              <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.semibold }} noWrap>
                {emp.first_name} {emp.last_name}
              </Typography>
              <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary' }} noWrap>
                {emp.designation || 'Team Member'}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
}

// ── Main Page ──

export default function MyOnboardingPage() {
  const user = useAuthStore((s) => s.user);
  const companies = useAuthStore((s) => s.companies);
  const activeCompanyId = useAuthStore((s) => s.activeCompanyId);
  const activeCompany = companies.find((c) => c.company_id === activeCompanyId);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotificationStore();
  const [activePhase, setActivePhase] = useState(0);
  const [completingId, setCompletingId] = useState<string | null>(null);

  const { data: instance, isLoading } = useQuery({
    queryKey: ['my-onboarding'],
    queryFn: () => onboardingApi.getMyOnboarding(),
    select: (res) => res.data as OnboardingInstance | null,
  });

  // Fetch employee profile for designation/position
  const { data: profileData } = useQuery<{ profile: { designation?: string; department_name?: string; date_of_hire?: string } }>({
    queryKey: ['employee-portal'],
    queryFn: () => apiClient.get('/employee/my-info').then((r) => r.data),
    retry: false,
  });
  const position = profileData?.profile?.designation;
  const department = profileData?.profile?.department_name;
  const hireDate = profileData?.profile?.date_of_hire;

  const completeMutation = useMutation({
    mutationFn: ({ taskId, formData }: { taskId: string; formData?: Record<string, unknown> }) =>
      onboardingApi.completeTask(taskId, { form_data: formData }),
    onMutate: ({ taskId }) => setCompletingId(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-onboarding'] });
      showSuccess('Nice work! Task completed.');
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to complete task';
      showError(msg);
    },
    onSettled: () => setCompletingId(null),
  });

  const uploadMutation = useMutation({
    mutationFn: ({ taskId, file }: { taskId: string; file: File }) =>
      onboardingApi.uploadDocument(taskId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-onboarding'] });
      showSuccess('Document uploaded successfully!');
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Upload failed';
      showError(msg);
    },
  });

  // Computed
  const tasksByPhase = useMemo(() => {
    if (!instance?.tasks) return {};
    return PHASES.reduce((acc, p) => {
      acc[p.key] = instance.tasks.filter(t => t.category === p.key);
      return acc;
    }, {} as Record<string, OnboardingTask[]>);
  }, [instance]);

  const activePhases = PHASES.filter(p => (tasksByPhase[p.key] || []).length > 0);
  const currentPhase = activePhases[activePhase] || activePhases[0];

  // Stats
  const progress = instance?.progress_pct || 0;
  const isComplete = instance?.status === 'completed';
  const overdueTasks = instance?.tasks?.filter(isOverdue).length || 0;
  const myPendingTasks = instance?.tasks?.filter(
    t => t.assignee_type === 'employee' && t.status !== 'completed' && t.status !== 'skipped'
  ).length || 0;

  // Loading
  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rounded" height={80} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={40} sx={{ mb: 2, borderRadius: 2 }} />
        {[1, 2, 3].map(i => (
          <Skeleton key={i} variant="rounded" height={64} sx={{ mb: 1.5, borderRadius: 2 }} />
        ))}
      </Box>
    );
  }

  // No onboarding
  if (!instance) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <DoneIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h5" fontWeight={TYPE.bold} sx={{ mb: 1 }}>
          All caught up!
        </Typography>
        <Typography color="text.secondary">
          You don't have any active onboarding tasks.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Welcome Header ── */}
      <Box
        sx={{
          mb: 2, p: { xs: 2, md: 2.5 }, borderRadius: 2,
          background: (t) => `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.05)} 0%, ${alpha(t.palette.success.main, 0.05)} 100%)`,
          border: '1px solid', borderColor: 'divider',
        }}
      >
        {isComplete ? (
          <Stack direction="row" alignItems="center" gap={1.5}>
            <CelebrationIcon sx={{ fontSize: 28, color: ONBOARDING_PHASE_COLORS[2] }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={TYPE.bold}>
                Congratulations, {user?.first_name}!
              </Typography>
              <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary' }}>
                All onboarding tasks complete. Your HR team will confirm probation start shortly.
              </Typography>
            </Box>
            <ProgressRing value={progress} size={56} />
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" gap={2}>
            <ProgressRing value={progress} size={64} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="h6" fontWeight={TYPE.bold} sx={{ lineHeight: 1.3 }}>
                Welcome, {user?.first_name}!
              </Typography>
              <PageHelpIcon pageKey="onboarding" />
            </Stack>
              <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary', lineHeight: 1.4 }}>
                {position
                  ? `${position}${department ? ` — ${department}` : ''}${activeCompany?.company_name ? ` at ${activeCompany.company_name}` : ''}`
                  : activeCompany?.company_name || 'Welcome aboard!'
                }
                {hireDate && ` · Starts ${formatDate(hireDate)}`}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                {myPendingTasks > 0 && (
                  <Chip
                    icon={<ArrowIcon sx={{ fontSize: '14px !important' }} />}
                    label={`${myPendingTasks} task${myPendingTasks > 1 ? 's' : ''} for you`}
                    color="primary" variant="filled" size="small"
                    sx={{ fontWeight: TYPE.semibold, height: 24, fontSize: TYPE.xs }}
                  />
                )}
                {overdueTasks > 0 && (
                  <Chip
                    icon={<OverdueIcon sx={{ fontSize: '14px !important' }} />}
                    label={`${overdueTasks} overdue`}
                    color="error" variant="filled" size="small"
                    sx={{ fontWeight: TYPE.semibold, height: 24, fontSize: TYPE.xs }}
                  />
                )}
              </Stack>
            </Box>
          </Stack>
        )}
      </Box>

      {/* ── How this works (collapsible) ── */}
      {!isComplete && (
        <Accordion
          disableGutters
          sx={{
            mb: 2, borderRadius: '8px !important', border: '1px solid', borderColor: 'divider',
            bgcolor: (t) => alpha(t.palette.info.main, 0.03), '&:before': { display: 'none' },
            boxShadow: 'none',
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
            <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.semibold, color: 'text.secondary' }}>
              How this works
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 2 }}>
            <Stack spacing={0.5}>
              {[
                'Work through each phase tab — start with Pre-Start before your first day.',
                'Upload documents where required. HR will review and approve them.',
                'Tasks marked "You" are yours. "People", "Manager", or "IT" tasks are handled by your team.',
                'Use the notes section on any task to ask questions.',
              ].map((text, i) => (
                <Typography key={i} sx={{ fontSize: TYPE.xs, color: 'text.secondary', lineHeight: 1.5 }}>
                  {i + 1}. {text}
                </Typography>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/* ── Phase Tabs (with counts) ── */}
      <Tabs
        value={activePhase}
        onChange={(_, v) => setActivePhase(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2, minHeight: 40,
          '& .MuiTab-root': {
            textTransform: 'none', fontWeight: TYPE.semibold, fontSize: TYPE.sm,
            minHeight: 40, py: 0.5,
          },
        }}
      >
        {activePhases.map((p) => {
          const tasks = tasksByPhase[p.key] || [];
          const done = tasks.filter(t => t.status === 'completed' || t.status === 'skipped').length;
          const allDone = done === tasks.length && tasks.length > 0;
          return (
            <Tab
              key={p.key}
              label={
                <Stack direction="row" alignItems="center" gap={0.75}>
                  {allDone ? <DoneIcon sx={{ fontSize: 16, color: 'success.main' }} /> : p.icon}
                  <span>{p.label}</span>
                  <Typography component="span" sx={{ fontSize: TYPE.xs, color: 'text.disabled', fontWeight: TYPE.medium }}>
                    {done}/{tasks.length}
                  </Typography>
                </Stack>
              }
            />
          );
        })}
      </Tabs>

      {/* ── Task List ── */}
      <Stack spacing={1}>
        {(tasksByPhase[currentPhase?.key] || []).map(task => (
          <Fade in key={task.id}>
            <Box>
              <TaskItem
                task={task}
                phaseColor={currentPhase?.color || ONBOARDING_PHASE_COLORS[0]}
                onComplete={(taskId, formData) => completeMutation.mutate({ taskId, formData })}
                onUpload={(taskId, file) => uploadMutation.mutate({ taskId, file })}
                isCompletePending={completingId === task.id}
              />
            </Box>
          </Fade>
        ))}
      </Stack>

      {/* ── Empty state for completed phase ── */}
      {currentPhase && (tasksByPhase[currentPhase.key] || []).every(
        t => t.status === 'completed' || t.status === 'skipped'
      ) && (tasksByPhase[currentPhase.key] || []).length > 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <DoneIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
          <Typography variant="h6" fontWeight={TYPE.bold} sx={{ mb: 0.5 }}>
            {currentPhase.label} — All Done!
          </Typography>
          <Typography color="text.secondary">
            Great work. Move to the next phase to continue.
          </Typography>
          {activePhase < activePhases.length - 1 && (
            <Button
              variant="outlined" endIcon={<ArrowIcon />}
              onClick={() => setActivePhase(activePhase + 1)}
              sx={{ mt: 2, borderRadius: 6, textTransform: 'none', fontWeight: TYPE.semibold }}
            >
              Next: {activePhases[activePhase + 1]?.label}
            </Button>
          )}
        </Box>
      )}

      {/* ── Meet Your Team ── */}
      <TeamIntroduction />
    </Box>
  );
}
